import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Grid,
  Tooltip,
  Button,
  CircularProgress,
  Avatar,
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { toast } from "react-toastify";
import { useThemeMode } from "../../themeMode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImages } from "@fortawesome/free-solid-svg-icons";
import { API_URL, AVATARS_BUCKET } from "../../config";
import UsersApi from "../../services/api/users";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";

const SettingsProfile = ({ data, user, token, AWS, isLoading }) => {
  const isMountedRef = useRef(true);
  const [firstName, setFirstName] = useState(data?.user_metadata?.firstName || '');
  const [lastName, setLastName] = useState(data?.user_metadata?.lastName || '');
  const [copyright, setCopyright] = useState(data?.user_metadata?.copyright || '');
  const [userName, setUserName] = useState(data?.user_metadata?.username || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [monthlyEnabled, setMonthlyEnabled] = useState(false);
  const { mode, setMode } = useThemeMode();
  const [themeMode, setThemeModeLocal] = useState(mode);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (data?.user_metadata) {
      setFirstName(data.user_metadata.firstName || '');
      setLastName(data.user_metadata.lastName || '');
      setCopyright(data.user_metadata.copyright || '');
      setUserName(data.user_metadata.username || '');
    }
  }, [data]);

  useEffect(() => {
    if (token) {
      UsersApi.getMonthlyDistributionSetting(token).then((res) => {
        if (isMountedRef.current && res?.data?.app_metadata?.monthlyDistributionEnabled !== undefined) {
          setMonthlyEnabled(!!res.data.app_metadata.monthlyDistributionEnabled);
        }
      }).catch(() => {});
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      UsersApi.getThemeMode(token).then((res) => {
        if (!isMountedRef.current) return;
        const serverMode = res?.data?.app_metadata?.themeMode;
        if (serverMode === "dark" || serverMode === "light") {
          setThemeModeLocal(serverMode);
          setMode(serverMode);
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    // Load the logo when component mounts or user changes
    if (user?.sub) {
      const logoUrl = `https://${AVATARS_BUCKET}.s3.us-east-1.amazonaws.com/${user.sub}.png`;
      setLogoUrl(logoUrl);
      setLogoLoaded(false);
      setLogoError(false);
    }
  }, [user?.sub]);

  const handleFirstname = (e) => {
    if (isMountedRef.current) setFirstName(e.target.value);
  };

  const handleLastname = (e) => {
    if (isMountedRef.current) setLastName(e.target.value);
  };

  const handleUsername = (e) => {
    if (isMountedRef.current) setUserName(e.target.value);
  };

  const uploadLogo = async (ev) => {
    console.log("Upload initiated");
    console.log("Event target:", ev.target);
    console.log("Files property:", ev.target.files);
    console.log("Files length:", ev.target.files ? ev.target.files.length : 'files is null/undefined');
    
    // Enhanced file selection check
    const files = ev.target.files;
    if (!files) {
      console.log("Files object is null/undefined");
      toast.warning("No files detected. Please try again.");
      return;
    }
    
    if (files.length === 0) {
      console.log("Files length is 0");
      toast.warning("Please select a file to upload");
      return;
    }

    const file = files[0];
    if (!file) {
      console.log("First file is null/undefined");
      toast.warning("Invalid file selected. Please try again.");
      return;
    }

    console.log("Selected file details:", {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    // Validate file type - be more flexible with PNG detection
    const isPNG = file.type === 'image/png' || 
                  file.type === 'image/PNG' || 
                  file.name.toLowerCase().endsWith('.png');
    
    if (!isPNG) {
      toast.error("Please select a PNG file");
      ev.target.value = "";
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      ev.target.value = "";
      return;
    }

    // Validate minimum file size (at least 1KB)
    if (file.size < 1024) {
      toast.error("File seems too small. Please select a valid PNG image.");
      ev.target.value = "";
      return;
    }

    setLogoUploading(true);
    setLogoLoaded(false);
    setLogoError(false);

    try {
      // Check if AWS credentials are available
      console.log("AWS config:", AWS?.config);
      console.log("AWS credentials available:", !!AWS?.config?.credentials);
      
      if (!AWS?.config?.credentials) {
        throw new Error("AWS credentials not available. Please ensure you're logged in and try refreshing the page.");
      }

      const s3 = new S3Client({
        region: "us-east-1",
        credentials: AWS.config.credentials,
      });

      const key = `${user.sub}.png`;
      
      // Convert file to a format compatible with AWS SDK
      let bodyData;
      try {
        console.log("Converting file to ArrayBuffer...");
        const fileBuffer = await file.arrayBuffer();
        console.log("File converted to ArrayBuffer, size:", fileBuffer.byteLength);
        
        // Create a Uint8Array from the ArrayBuffer - this is most compatible with AWS SDK
        bodyData = new Uint8Array(fileBuffer);
        console.log("Created Uint8Array from ArrayBuffer");
      } catch (bufferError) {
        console.error("Failed to convert file:", bufferError);
        throw new Error("Failed to process the selected file. Please try a different image.");
      }
      
      const s3params = {
        Body: bodyData,
        Bucket: AVATARS_BUCKET,
        Key: key,
        ServerSideEncryption: "AES256",
        ContentType: "image/png",
        ACL: "public-read", // Make logos publicly accessible for email usage
      };

      console.log("Uploading to S3:", { 
        bucket: AVATARS_BUCKET, 
        key,
        fileSize: file.size
      });
      
      const command = new PutObjectCommand(s3params);
      await s3.send(command);
      
      console.log("Upload successful");
      toast.success("Logo updated successfully");

      // Update logo URL with cache busting for immediate display
      const timestamp = Date.now();
      const newLogoUrl = `https://${AVATARS_BUCKET}.s3.us-east-1.amazonaws.com/${key}?t=${timestamp}`;
      
      if (isMountedRef.current) {
        setLogoUrl(newLogoUrl);
        setLogoUploading(false);
        setLogoError(false);
        // logoLoaded will be set to true by the onLoad event
      }
    } catch (err) {
      console.error("Upload error:", err);
      console.error("Error details:", {
        name: err.name,
        message: err.message,
        stack: err.stack,
        code: err.code
      });
      
      let errorMessage = "Upload failed";
      if (err.message.includes("credentials")) {
        errorMessage = "Authentication error. Please refresh the page and try again.";
      } else if (err.name === 'NetworkingError' || err.message.includes("network")) {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (err.message.includes("getReader") || err.message.includes("readableStream")) {
        errorMessage = "Browser compatibility issue. Please try refreshing the page or using a different browser.";
      } else if (err.message.includes("process") || err.message.includes("convert")) {
        errorMessage = err.message; // Use our custom error message for file processing
      } else if (err.name === 'InvalidAccessKeyId' || err.name === 'SignatureDoesNotMatch') {
        errorMessage = "AWS authentication error. Please log out and log back in.";
      } else if (err.name === 'AccessDenied' || err.message.includes("AccessDenied") || err.message.includes("ACL")) {
        errorMessage = "Permission error: Unable to set public access. The upload may succeed but image may not be visible.";
      } else if (err.message) {
        errorMessage = `Upload failed: ${err.message}`;
      }
      
      toast.error(errorMessage);
      
      // On error, clear the logo and show error state
      if (isMountedRef.current) {
        setLogoUrl("");
        setLogoUploading(false);
        setLogoError(true);
      }
    }
    
    // Clear the input to allow re-selecting the same file
    ev.target.value = "";
  };

  const handleCopyright = (e) => {
    if (isMountedRef.current) setCopyright(e.target.value);
  };

  const handleMonthlyToggle = async (e) => {
    const newValue = e.target.checked;
    setMonthlyEnabled(newValue);
    try {
      await UsersApi.updateMonthlyDistributionSetting(token, newValue);
      toast.success(`Monthly distribution ${newValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setMonthlyEnabled(!newValue);
      toast.error("Failed to update monthly distribution setting");
    }
  };

  const handleThemeModeToggle = async (e) => {
    const next = e.target.checked ? "dark" : "light";
    const previous = themeMode;
    setThemeModeLocal(next);
    setMode(next);
    try {
      await UsersApi.updateThemeMode(token, next);
      toast.success(`Theme set to ${next} mode`);
    } catch (err) {
      setThemeModeLocal(previous);
      setMode(previous);
      toast.error("Failed to save theme preference");
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();

    if (!userName) {
      toast.warning("Please check: Username is required!");
      return;
    }

    try {
      setIsUpdating(true);
      const body = JSON.stringify({
        type: "update",
        firstName: firstName,
        lastName: lastName,
        username: userName,
        copyright: copyright,
      });

      const response = await fetch(
        API_URL + "/users/v1/profile",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: body,
          method: "POST",
        }
      );

      if (response.ok) {
        toast.success("Profile Updated");
      } else {
        const errorData = await response.json();
        toast.error(`Update Failed: ${errorData.message || 'Unknown error'}`);
      }

      setIsUpdating(false);
    } catch (err) {
      console.error('Profile update error:', err);
      toast.error("Update Failed: " + err.message);
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: "left", pt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 4 }}>
          View or update your details:
        </Typography>
        <form onSubmit={submitForm}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <Tooltip
                title="This is used as your name when sending out rewards."
                placement="top"
              >
                <TextField
                  fullWidth
                  label="Username"
                  id="username"
                  required
                  onChange={handleUsername}
                  value={userName}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="First Name"
                id="firstName"
                onChange={handleFirstname}
                value={firstName}
                helperText=""
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Last Name"
                id="lastName"
                onChange={handleLastname}
                value={lastName}
                helperText=""
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Copyright"
                id="copyright"
                multiline
                rows={5}
                onChange={handleCopyright}
                value={copyright}
                helperText="Optional - Used for photo metadata"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <hr />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Button
                type="submit"
                variant="contained"
                disabled={isUpdating}
                id="updateDetails"
              >
                {isUpdating ? (
                  <CircularProgress size={24} />
                ) : (
                  "Update Details"
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Distribution Settings
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={monthlyEnabled}
                onChange={handleMonthlyToggle}
              />
            }
            label="Monthly Distribution"
          />
        </FormGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          When enabled, collections can be assigned to a specific month for scheduled distribution.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Appearance
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={themeMode === "dark"}
                onChange={handleThemeModeToggle}
                inputProps={{ "aria-label": "Toggle dark mode" }}
              />
            }
            label="Dark mode"
          />
        </FormGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Switch the dashboard between light and dark. Your preference is saved
          to your account and applied on every device.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }} id="uploadLogoArea">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Typography variant="h6">Creator Logo</Typography>
          </Grid>
          <Grid item>
            <Button
              id="uploadLogoButton"
              variant="outlined"
              size="small"
              disabled={logoUploading}
              onClick={(ev) => {
                ev.preventDefault();
                const fileInput = document.getElementById("uploadLogo");
                if (fileInput) {
                  fileInput.click();
                } else {
                  console.error("File input not found");
                  toast.error("Upload button not working. Please refresh the page.");
                }
              }}
              startIcon={
                logoUploading ? (
                  <CircularProgress size={20} />
                ) : (
                  <FontAwesomeIcon icon={faImages} />
                )
              }
            >
              {logoUploading
                ? "Uploading..."
                : "Upload (PNG only)"}
            </Button>
          </Grid>
        </Grid>
        <Grid container justifyContent="center" sx={{ my: 4 }}>
          <Grid item>
            <input
              id="uploadLogo"
              type="file"
              name="logoFile"
              onChange={(e) => {
                console.log("File input onChange triggered");
                uploadLogo(e);
              }}
              style={{ display: 'none' }}
              accept="image/png,.png"
              multiple={false}
            />
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              {logoUploading ? (
                <Box 
                  sx={{ 
                    width: 250, 
                    height: 250, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    border: '2px dashed #ccc',
                    borderRadius: 2
                  }}
                >
                  <CircularProgress size={60} />
                </Box>
              ) : (
                <Avatar
                  src={logoUrl}
                  sx={{ 
                    width: 250, 
                    height: 250,
                    border: logoError ? 'none' : '2px solid #e0e0e0'
                  }}
                  variant="rounded"
                  onLoad={() => {
                    if (isMountedRef.current) {
                      setLogoLoaded(true);
                      setLogoError(false);
                    }
                  }}
                  onError={() => {
                    if (isMountedRef.current) {
                      setLogoLoaded(false);
                      setLogoError(true);
                    }
                  }}
                >
                  {logoError && (
                    <FontAwesomeIcon 
                      icon={faImages} 
                      style={{ fontSize: '60px', color: '#ccc' }} 
                    />
                  )}
                </Avatar>
              )}
              {logoError && !logoUploading && (
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    position: 'absolute', 
                    bottom: -20, 
                    left: '50%', 
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  No logo uploaded yet
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SettingsProfile;
